import {
  Box,
  Card,
  CardContent,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { MemoPhoneInput } from 'components/common/simpleInputFields/phoneInput';
import { useOrganizations } from 'store/organizations';
import { ButtonContained } from 'components/general';
import { MuiTelInputInfo } from 'mui-tel-input';
import React, { useEffect } from 'react';
import { FormattedMessage, formatTranslation } from 'intl';
import { UserTranslationKeys, CommonTranslationKeys, ValidationTranslationKeys } from 'intl/keys';
import { useIntl } from 'react-intl';
import { useCurrentUser } from 'store/user';
import { useProjectOrganizationId } from 'store/project';
import { PersonalData } from 'shared/domain/user/types/entity';
import { FieldSize } from 'shared/types/fieldSize';
import useFormEdit from 'views/issue/forms/useFormEdit';
import { useUserStore } from 'store/user';
import { showChannelRequestErrorToast } from 'services/toaster';

const LABEL_MAX_LENGTH = FieldSize.M;

export const CreatableUser = (): React.ReactElement => {
  const formName = 'currentUser';
  const organizationId = useProjectOrganizationId();
  const { organizationsMap } = useOrganizations();
  const currentOrganization = organizationsMap.get(organizationId);
  const user = useCurrentUser();
  const { setEditing } = useFormEdit();
  const intl = useIntl();
  const { updatePersonalData } = useUserStore.use.actions();

  const [formData, setFormData] = React.useState<PersonalData>({
    clientId: organizationId,
    label: '',
    phone: '',
  });

  const [isNewEntry, setIsNewEntry] = React.useState(true);
  const [initialData, setInitialData] =
    React.useState<PersonalData | null>(null);
  const [labelError, setLabelError] = React.useState('');

  useEffect(() => {
    if (user.personalData) {
      const existingData = user.personalData.find(
        (data) => data.clientId === organizationId
      );

      if (existingData) {
        setFormData(existingData);
        setInitialData(existingData);
        setIsNewEntry(false);
      } else {
        setFormData({
          clientId: organizationId,
          label: '',
          phone: '',
        });
        setInitialData(null);
        setIsNewEntry(true);
      }
    }
  }, [user.personalData, organizationId]);

  const validateLabel = (value: string): string => {
    if (value?.length > LABEL_MAX_LENGTH) {
      return formatTranslation(intl, ValidationTranslationKeys.stringLengthMaximumWeb, { maxLength: LABEL_MAX_LENGTH });
    }
    return '';
  };

  const handleLabelChange = (value: string): void => {
    const error = validateLabel(value);
    setLabelError(error);

    setFormData((prev) => ({
      ...prev,
      label: value || '',
    }));
    setEditing(true, formName);
  };

  const handlePhoneChange = (
    e: any,
    newValue: string,
    _: MuiTelInputInfo
  ): void => {
    setFormData((prev) => ({
      ...prev,
      phone: newValue,
    }));
    setEditing(true, formName);
  };

  const hasValidationErrors = (): boolean => {
    return Object.values(labelError).some((error) => error !== '');
  };

  const hasModifications = (): boolean => {
    if (isNewEntry) {
      return formData.label !== '' || formData.phone !== '';
    }
    return (
      formData.label !== initialData?.label ||
      formData.phone !== initialData?.phone
    );
  };

  const handleSave = async (): Promise<void> => {
    try {
      await updatePersonalData({
        userId: user._id,
        organizationId,
        personalData: { label: formData.label, phone: formData.phone },
      });

      setEditing(false, formName);
      setInitialData(formData);
      setIsNewEntry(false);
    } catch (error) {
      showChannelRequestErrorToast('Failed to update user data', error);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        maxWidth: '600px',
        margin: '0 auto',
        height: '100%',
        p: 3,
      }}
    >
      <Card
        elevation={3}
        sx={{
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            p: 3,
          }}
        >
          <Typography variant='h5' sx={{ fontWeight: 500 }}>
            <FormattedMessage id={UserTranslationKeys.viewHeader} />
          </Typography>
        </Box>

        <Divider />

        <CardContent sx={{ p: 3 }}>
          <Stack spacing={3}>
            <Typography
              variant='body1'
              color='text.secondary'
              sx={{
                fontSize: '0.95rem',
                fontStyle: 'italic',
                mb: 2,
              }}
            >
              {formatTranslation(intl, UserTranslationKeys.viewDataMessage, { organizationLabel: currentOrganization?.label })}
            </Typography>

            <TextField
              fullWidth
              variant='outlined'
              value={formData.label || ''}
              onChange={(e) => handleLabelChange(e.target.value)}
              data-qa='user-view-label-input'
              label={formatTranslation(intl, UserTranslationKeys.viewNameInputLabel,)}
              error={!!labelError}
              helperText={
                labelError ||
                `${formData.label?.length || 0}/${LABEL_MAX_LENGTH}`
              }
              inputProps={{ maxLength: LABEL_MAX_LENGTH }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1.5,
                },
                pb: 2,
              }}
            />
            <MemoPhoneInput
              // fullWidth
              localisedLabel={formatTranslation(intl, UserTranslationKeys.phoneNumber,)}
              fieldName='phone'
              labelId={UserTranslationKeys.phoneNumber}
              value={formData.phone}
              handleChange={handlePhoneChange}
              dense={true}
              error=''
              reserveSpaceForHelperText={true}
            />

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                mt: 3,
              }}
            >
              <ButtonContained
                onClick={handleSave}
                disabled={!hasModifications() || hasValidationErrors()}
                data-qa='user-view-save-button'
                sx={{
                  minWidth: 120,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontSize: '1rem',
                }}
              >
                <FormattedMessage id={CommonTranslationKeys.save} />
              </ButtonContained>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};
